import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import {
    Calendar,
    ChevronLeft,
    ChevronRight,
    Plus,
    AlignLeft,
    X,
    Trash2,
    Share2,
    Clipboard,
    ListFilter,
    AlertCircle,
    MoreHorizontal,
    Gift,
    PartyPopper,
    Flame,
    Sparkles,
    StickyNote,
    RotateCcw
} from "lucide-react";
import CustomDatePicker from "../components/CustomDatePicker";
import ScheduleDetailModal from "../components/ScheduleDetailModal";
import { useModal } from "../contexts/ModalContext";
import { DEFAULT_THEME } from "../constants/theme";
import ScheduleCardItem from "./schedule/ScheduleCardItem";
import ScheduleFormFields from "./schedule/ScheduleFormFields";
import ScheduleListItem from "./schedule/ScheduleListItem";
import ScheduleMobileScheduleList from "./schedule/ScheduleMobileScheduleList";
import ScheduleToolbarControls from "./schedule/ScheduleToolbarControls";
import MobileFloatingActionButton from "./common/MobileFloatingActionButton";

// [3D 아이콘]
const ICONS = {
    calendar: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Calendar.png",
    memo: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Memo.png",
    pushpin: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Pushpin.png",
    check: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Symbols/Check%20Mark%20Button.png",
    bible: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Books.png", // Worship
    note: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Spiral%20Notepad.png", // Meeting
    coffee: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Food/Hot%20Beverage.png", // Fellowship
    balloon: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Activities/Balloon.png", // Event
    cake: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Food/Birthday%20Cake.png", // Birthday
    fire: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Travel%20and%20places/Fire.png" // D-Day High Priority
};

// [NEW] 한국 공휴일 데이터 (2024-2025)
const HOLIDAYS = {
    "1-1": "신정",
    "3-1": "삼일절",
    "5-5": "어린이날",
    "6-6": "현충일",
    "8-15": "광복절",
    "10-3": "개천절",
    "10-9": "한글날",
    "12-25": "성탄절",
    // 음력 휴일 (2025년 기준 근사치)
    "2025-1-28": "설날 연휴",
    "2025-1-29": "설날",
    "2025-1-30": "설날 연휴",
    "2025-5-5": "부처님오신날", // 어린이날과 겹침
    "2025-10-6": "추석",
    "2025-10-7": "추석 연휴",
    "2025-10-8": "추석 연휴",
};

// [NEW] Theme Tooltip Component


export default function ScheduleView({
    schedules,
    students = [],
    teachers = [],
    onAdd,
    onUpdate,
    onDelete,
    selectedDepartment,
    userProfile,
    theme = DEFAULT_THEME,
    onNavigateToMemberDetail
}) {
    // --- 상태 관리 ---
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const { openConfirm, openAlert } = useModal();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // [NEW] 스와이프 제스처 상태
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const minSwipeDistance = 200;

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onCalendarSwipeEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            handleNextMonth(); // 다음 달로 이동
        } else if (isRightSwipe) {
            handlePrevMonth(); // 지난 달로 이동
        }
    };

    const onModalSwipeEnd = (closeHandler) => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isRightSwipe) {
            closeHandler();
        }
    };

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState(null);

    const [viewingSchedule, setViewingSchedule] = useState(null);
    const [sharingSchedule, setSharingSchedule] = useState(null);

    // [NEW] 모달 드래그 로직 (Modal Dragging Logic)
    const [modalPos, setModalPos] = useState({ x: 0, y: 0 });
    const isDraggingRef = useRef(false);
    const dragStartRef = useRef({ x: 0, y: 0 });

    useEffect(() => {
        if (isModalOpen) {
            setModalPos({ x: 0, y: 0 }); // 모달이 열릴 때 위치 초기화
            // [NEW] 바디 스크롤 방지
            document.body.style.overflow = 'hidden';
        } else {
            // [NEW] 바디 스크롤 복원
            document.body.style.overflow = 'auto';
        }
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isModalOpen]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDraggingRef.current) return;
            e.preventDefault();
            const dx = e.clientX - dragStartRef.current.startX;
            const dy = e.clientY - dragStartRef.current.startY;
            setModalPos({
                x: dragStartRef.current.initialX + dx,
                y: dragStartRef.current.initialY + dy
            });
        };

        const handleMouseUp = () => {
            isDraggingRef.current = false;
        };

        if (isModalOpen) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isModalOpen]);

    const handleHeaderMouseDown = (e) => {
        if (isMobile) return;
        isDraggingRef.current = true;
        dragStartRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            initialX: modalPos.x,
            initialY: modalPos.y
        };
    };

    const [selectedType, setSelectedType] = useState('all');
    const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'card'
    const [isBirthdaySheetOpen, setIsBirthdaySheetOpen] = useState(false);

    const [formData, setFormData] = useState({
        title: "",
        date: "",
        startTime: "",
        endTime: "",
        location: "",
        description: "",
        type: "worship",
        priority: "medium", // high, medium, low
        checklists: [], // [{ id: 1, text: "...", checked: false }]
        isCompleted: false
    });

    const [newChecklistInput, setNewChecklistInput] = useState("");

    // [NEW] Long press detection for mobile
    const [longPressTimer, setLongPressTimer] = useState(null);

    // [NEW] Double tap detection for mobile
    const lastTapRef = useRef({ time: 0, dateStr: null });
    const DOUBLE_TAP_DELAY = 300; // 300ms 이내 두 번 탭하면 더블탭

    const handleDateTap = (dateStr) => {
        const now = Date.now();
        const lastTap = lastTapRef.current;

        // 같은 날짜를 300ms 이내에 두 번 탭하면 더블탭
        if (lastTap.dateStr === dateStr && now - lastTap.time < DOUBLE_TAP_DELAY) {
            // 더블탭 - 일정 등록창 열기
            handleDayDoubleClick(new Date(dateStr));
            lastTapRef.current = { time: 0, dateStr: null }; // 리셋
        } else {
            // 싱글탭 - 날짜 선택
            lastTapRef.current = { time: now, dateStr };
            setSelectedDate(isSameDay(selectedDate, dateStr) ? null : dateStr);
        }
    };

    const handleDateLongPressStart = (dateStr) => {
        const timer = setTimeout(() => {
            setFormData(prev => ({ ...prev, date: dateStr }));
            openAddModal();
        }, 500); // 500ms long press
        setLongPressTimer(timer);
    };

    const handleDateLongPressEnd = () => {
        if (longPressTimer) {
            clearTimeout(longPressTimer);
            setLongPressTimer(null);
        }
    };

    // [ESC 키 감지]
    useEffect(() => {
        const handleEsc = (event) => {
            if (event.key === "Escape") {
                if (isModalOpen) {
                    const isDirty = formData.title || formData.location || formData.description;
                    if (isDirty) {
                        openConfirm("작성 취소", "작성 중인 내용이 있습니다. 정말 닫으시겠습니까?", () => {
                            setIsModalOpen(false);
                        });
                    } else {
                        setIsModalOpen(false);
                    }
                    return;
                }
                if (viewingSchedule) setViewingSchedule(null);
                if (sharingSchedule) setSharingSchedule(null);
            }
        };
        window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, [isModalOpen, viewingSchedule, sharingSchedule, formData, openConfirm]);

    // --- [핵심] 날짜 포맷 헬퍼 ---
    const formatDateLocal = (date) => {
        if (!date) return "";
        // If date is already a string, return it
        if (typeof date === 'string') return date;
        // Ensure date is a Date object
        const dateObj = date instanceof Date ? date : new Date(date);
        const y = dateObj.getFullYear();
        const m = String(dateObj.getMonth() + 1).padStart(2, '0');
        const d = String(dateObj.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    // --- 날짜 계산 헬퍼 ---
    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const lastDate = new Date(year, month + 1, 0).getDate();

        const days = [];
        // Empty slots for previous month
        for (let i = 0; i < firstDay; i++) days.push(null);
        // Days of current month
        for (let i = 1; i <= lastDate; i++) days.push(new Date(year, month, i));
        // Pad remaining slots to complete the week (multiple of 7)
        const totalSlots = days.length;
        const remainder = totalSlots % 7;
        if (remainder !== 0) {
            const remainingCells = 7 - remainder;
            for (let i = 0; i < remainingCells; i++) days.push(null);
        }

        return days;
    };

    // --- 날짜 비교 헬퍼 ---
    const isSameDay = (d1, d2) => {
        if (!d1 || !d2) return false;
        // Convert to Date objects if they're strings
        const date1 = d1 instanceof Date ? d1 : new Date(d1);
        const date2 = d2 instanceof Date ? d2 : new Date(d2);

        return date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate();
    };

    const getHolidayName = (date) => {
        if (!date) return null;
        const monthDay = `${date.getMonth() + 1}-${date.getDate()}`;
        const yearMonthDay = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
        return HOLIDAYS[yearMonthDay] || HOLIDAYS[monthDay];
    };

    // [NEW] D-Day Logic (High Priority Events)
    const dDayEvents = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return schedules
            .filter(s => s.priority === 'high' && !s.isCompleted)
            .map(s => {
                const eventDate = new Date(s.date);
                eventDate.setHours(0, 0, 0, 0);
                const diffTime = eventDate - today;
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                return { ...s, dDay: diffDays };
            })
            .filter(s => s.dDay >= 0)
            .sort((a, b) => a.dDay - b.dDay)
            .slice(0, 3); // Top 3
    }, [schedules]);

    // [MODIFIED] Birthday Logic - 이번 달 생일자
    const upcomingBirthdays = useMemo(() => {
        const currentMonthStr = String(new Date().getMonth() + 1).padStart(2, "0");

        let filteredStudents = students;
        let filteredTeachers = teachers;

        if (selectedDepartment && selectedDepartment !== 'All') {
            filteredStudents = students.filter(s => (s.department || s.grade) === selectedDepartment);
            filteredTeachers = teachers.filter(t => t.charge && t.charge.includes(selectedDepartment));
        }

        const allPeople = [
            ...filteredStudents.map(s => ({ ...s, type: 'student' })),
            ...filteredTeachers.map(t => ({ ...t, type: 'teacher' }))
        ];

        // [FIX] 대시보드와 동일한 파싱 로직 - 다양한 날짜 형식 지원
        return allPeople.filter(p => {
            if (!p.birth) return false;

            // birth 필드 파싱: YYYY.MM.DD, YYYY-MM-DD, MM/DD 등 다양한 형식 지원
            const birthStr = typeof p.birth === 'string' ? p.birth :
                (p.birth?.toDate ? p.birth.toDate().toISOString().split('T')[0] : String(p.birth));

            // 다양한 구분자 처리: . - /
            const parts = birthStr.split(/[.\-\/]/);
            if (parts.length >= 2) {
                // YYYY.MM.DD or YYYY-MM-DD 형식이면 월은 index 1
                // MM.DD or MM/DD 형식이면 월은 index 0
                const monthPart = parts.length >= 3 ? parts[1] : parts[0];
                return monthPart?.trim()?.padStart(2, '0') === currentMonthStr;
            }
            return false;
        }).sort((a, b) => {
            // 생일 날짜 순으로 정렬
            const getDay = (birth) => {
                const birthStr = typeof birth === 'string' ? birth : String(birth);
                const parts = birthStr.split(/[.\-\/]/);
                const dayPart = parts.length >= 3 ? parts[2] : parts[1];
                return parseInt(dayPart, 10) || 0;
            };
            return getDay(a.birth) - getDay(b.birth);
        });
    }, [students, teachers, selectedDepartment]);

    const getBirthdayMonthDay = (birth) => {
        if (!birth) return null;
        const birthStr = typeof birth === 'string'
            ? birth
            : (birth?.toDate ? birth.toDate().toISOString().split('T')[0] : String(birth));
        const parts = birthStr.split(/[.\-\/]/);
        if (parts.length < 2) return null;

        const monthPart = parts.length >= 3 ? parts[1] : parts[0];
        const dayPart = parts.length >= 3 ? parts[2] : parts[1];
        const month = Number.parseInt(monthPart, 10);
        const day = Number.parseInt(dayPart, 10);
        if (Number.isNaN(month) || Number.isNaN(day)) return null;

        return { month, day };
    };

    const mobileMonthBirthdays = useMemo(() => {
        const currentMonth = currentDate.getMonth() + 1;

        let filteredStudents = students;
        let filteredTeachers = teachers;
        if (selectedDepartment && selectedDepartment !== 'All') {
            filteredStudents = students.filter(s => (s.department || s.grade) === selectedDepartment);
            filteredTeachers = teachers.filter(t => t.charge && t.charge.includes(selectedDepartment));
        }

        return [
            ...filteredStudents.map(s => ({ ...s, type: 'student' })),
            ...filteredTeachers.map(t => ({ ...t, type: 'teacher' }))
        ]
            .map(person => ({ ...person, birthday: getBirthdayMonthDay(person.birth) }))
            .filter(person => person.birthday && person.birthday.month === currentMonth)
            .sort((a, b) => a.birthday.day - b.birthday.day);
    }, [currentDate, students, teachers, selectedDepartment]);

    const mobileBirthdayDateSet = useMemo(() => {
        const set = new Set();
        mobileMonthBirthdays.forEach((person) => {
            const key = `${currentDate.getFullYear()}-${String(person.birthday.month).padStart(2, '0')}-${String(person.birthday.day).padStart(2, '0')}`;
            set.add(key);
        });
        return set;
    }, [currentDate, mobileMonthBirthdays]);

    const availableTypes = [
        { id: 'all', label: '전체', activeClass: `${theme.bg} ${theme.border} text-white` },
        { id: 'worship', label: '예배', activeClass: 'bg-purple-500 border-purple-500 text-white' },
        { id: 'event', label: '행사', activeClass: 'bg-orange-500 border-orange-500 text-white' },
        { id: 'meeting', label: '회의', activeClass: 'bg-blue-500 border-blue-500 text-white' },
        { id: 'fellowship', label: '교제', activeClass: 'bg-teal-500 border-teal-500 text-white' },
        { id: 'etc', label: '기타', activeClass: 'bg-gray-500 border-gray-500 text-white' }
    ];

    // [NEW] 부서별 색상 매핑 (Refined with User Preference)
    const getDepartmentColor = (dept) => {
        if (!dept) return 'bg-gray-500';

        // 1. Check if it matches User's Main Department
        if (userProfile && (userProfile.deptName === dept || (dept.includes(userProfile.deptName))) && userProfile.deptColor) {
            return userProfile.deptColor;
        }

        if (dept.includes('유아')) return 'bg-pink-400';
        if (dept.includes('유치')) return 'bg-yellow-400';
        if (dept.includes('유년')) return 'bg-orange-400';
        if (dept.includes('초등')) return 'bg-sky-500';
        if (dept.includes('중등')) return 'bg-lime-500';
        if (dept.includes('고등')) return 'bg-teal-500';
        if (dept.includes('청년')) return 'bg-indigo-500';
        if (dept.includes('장년')) return 'bg-purple-600';
        return 'bg-gray-500'; // 그외
    };

    // [NEW] 1. 기본 필터링 (Department & Filter Type) - 달력과 리스트 모두에 적용
    const filteredSchedulesBase = useMemo(() => {
        let targetSchedules = schedules;

        // 1-1. Type Filter
        if (selectedType !== 'all') {
            targetSchedules = targetSchedules.filter(s => s.type === selectedType);
        }

        // 1-2. Department Filter
        if (selectedDepartment && selectedDepartment !== 'All') {
            targetSchedules = targetSchedules.filter(s => !s.department || s.department === 'All' || s.department === selectedDepartment);
        }

        return targetSchedules;
    }, [schedules, selectedType, selectedDepartment]);

    // [NEW] 2. 날짜 필터링 (사이드바 리스트용)
    const filteredSchedules = useMemo(() => {
        let targetSchedules = filteredSchedulesBase;

        // 2-1. Date Filter
        if (selectedDate) {
            const dateStr = formatDateLocal(selectedDate);
            targetSchedules = targetSchedules.filter(s => s.date === dateStr);
        } else {
            const year = currentDate.getFullYear();
            const month = String(currentDate.getMonth() + 1).padStart(2, '0');
            const monthPrefix = `${year}-${month}`;
            targetSchedules = targetSchedules.filter(s => s.date.startsWith(monthPrefix));
        }

        return targetSchedules.sort((a, b) => {
            if (a.isCompleted !== b.isCompleted) return a.isCompleted ? 1 : -1;
            if (a.date !== b.date) return a.date.localeCompare(b.date);
            const pMap = { high: 0, medium: 1, low: 2 };
            const pA = pMap[a.priority || 'medium'];
            const pB = pMap[b.priority || 'medium'];
            if (pA !== pB) return pA - pB;
            return (a.startTime || "").localeCompare(b.startTime || "");
        });
    }, [filteredSchedulesBase, selectedDate, currentDate]);

    // --- 핸들러 ---
    const handlePrevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
        setSelectedDate(null);
    };
    const handleNextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
        setSelectedDate(null);
    };
    const handleToday = () => {
        const today = new Date();
        setCurrentDate(today);
        setSelectedDate(today);
    };

    const handleDateChange = (dateStr) => {
        const newDate = new Date(dateStr);
        setCurrentDate(newDate);
        setSelectedDate(newDate);
    };

    const handleDayClick = (date) => {
        if (date) setSelectedDate(date);
    };

    const handleDayDoubleClick = (date) => {
        if (date) {
            setSelectedDate(date);
            openAddModal(date);
        }
    };

    const openAddModal = (dateOverride = null) => {
        setEditingSchedule(null);
        let targetDate = new Date();
        if (dateOverride instanceof Date) targetDate = dateOverride;
        else if (selectedDate) targetDate = selectedDate;

        setFormData({
            title: "",
            date: formatDateLocal(targetDate),
            endDate: "", // [NEW] 종료일 초기화
            startTime: "",
            endTime: "",
            location: "",
            description: "",
            type: "worship",
            priority: "medium",
            checklists: [],
            isCompleted: false,
            department: selectedDepartment // [NEW] 부서 자동 입력
        });
        setNewChecklistInput("");
        setIsModalOpen(true);
    };

    const openEditModal = (schedule) => {
        setEditingSchedule(schedule);
        setFormData({
            ...schedule,
            endDate: schedule.endDate || "", // [NEW] 종료일 로드
            startTime: schedule.startTime || schedule.time || "",
            endTime: schedule.endTime || "",
            priority: schedule.priority || "medium",
            checklists: schedule.checklists || [],
            isCompleted: schedule.isCompleted || false
        });
        setNewChecklistInput("");
        setIsModalOpen(true);
        setViewingSchedule(null);
    };

    const switchToEdit = () => {
        const schedule = viewingSchedule;
        openEditModal(schedule);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const { time, ...dataToSave } = formData;
        if (editingSchedule) {
            onUpdate({ ...dataToSave, id: editingSchedule.id });
        } else {
            onAdd(dataToSave);
        }
        setIsModalOpen(false);
    };

    const handleAddChecklist = () => {
        if (!newChecklistInput.trim()) return;
        const newItem = { id: Date.now(), text: newChecklistInput, checked: false };
        setFormData(prev => ({ ...prev, checklists: [...prev.checklists, newItem] }));
        setNewChecklistInput("");
    };

    const handleRemoveChecklist = (id) => {
        setFormData(prev => ({ ...prev, checklists: prev.checklists.filter(item => item.id !== id) }));
    };

    const handleToggleChecklist = (id) => {
        setFormData(prev => ({
            ...prev,
            checklists: prev.checklists.map(item => item.id === id ? { ...item, checked: !item.checked } : item)
        }));
    };

    const handleToggleComplete = (e, schedule) => {
        e.stopPropagation();
        onUpdate({ ...schedule, isCompleted: !schedule.isCompleted });
    };

    const handleDetailChecklistToggle = (schedule, itemId) => {
        const updatedChecklists = (schedule.checklists || []).map(item =>
            item.id === itemId ? { ...item, checked: !item.checked } : item
        );
        const updatedSchedule = { ...schedule, checklists: updatedChecklists };
        onUpdate(updatedSchedule);
        setViewingSchedule(updatedSchedule);
    };

    const formatScheduleText = (s) => {
        const dateStr = s.endDate ? `${s.date} ~ ${s.endDate}` : s.date;
        const timeStr = s.startTime ? (s.endTime ? `${s.startTime} ~ ${s.endTime}` : s.startTime) : "시간 미정";
        const checklistStr = (s.checklists || []).map(c => `- [${c.checked ? 'v' : ' '}] ${c.text}`).join('\n');
        return `[일정 공유]\n제목: ${s.title}\n날짜: ${dateStr}\n시간: ${timeStr}\n장소: ${s.location || "-"}\n내용: ${s.description || "-"}\n\n[체크리스트]\n${checklistStr}`;
    };
    const handleCopySchedule = (s) => {
        const text = formatScheduleText(s);
        const textarea = document.createElement("textarea");
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        try {
            document.execCommand("copy");
            openAlert("일정이 복사되었습니다.", "알림");
        } catch (err) {
            openAlert("복사 실패", "오류");
        }
        document.body.removeChild(textarea);
        setSharingSchedule(null);
    };

    const getTypeName = (type) => {
        const found = availableTypes.find(t => t.id === type);
        return found ? found.label : '기타';
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'worship': return ICONS.bible;
            case 'event': return ICONS.balloon;
            case 'meeting': return ICONS.note;
            case 'fellowship': return ICONS.coffee;
            default: return ICONS.pushpin;
        }
    };

    const getTypeButtonClass = (type, isSelected) => {
        const baseClass = "flex-1 py-3 px-2 rounded-xl text-xs font-bold border transition whitespace-nowrap";
        if (!isSelected) return `${baseClass} bg-white text-gray-400 border-gray-200 hover:bg-gray-50`;
        return `${baseClass} ${availableTypes.find(t => t.id === type)?.activeClass || ''}`;
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'worship': return 'purple';
            case 'event': return 'orange';
            case 'meeting': return 'blue';
            case 'fellowship': return 'teal';
            default: return 'gray';
        }
    };




    const renderPriorityBadge = (priority) => {
        switch (priority) {
            case 'high': return <span className="text-[10px] font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded-full border border-red-100">긴급</span>;
            case 'low': return <span className="text-[10px] font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full border border-gray-200">여유</span>;
            default: return null;
        }
    };



    const renderEditModal = () => {
        if (!isModalOpen) return null;

        const mobileLayout = (
            <div
                className={`fixed inset-0 z-[9999] flex flex-col bg-white overflow-hidden`}
                style={{ zIndex: 9999 }}
            >
                <div className="flex-1 w-full bg-white relative z-10 flex flex-col min-h-0">
                    <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0 overflow-hidden">
                        {/* Mobile Header (Fixed & Swipeable) */}
                        <div
                            className="flex-none flex items-center justify-between px-4 border-b border-gray-100 bg-white z-20 pt-[calc(env(safe-area-inset-top)+36px)] pb-3 min-h-[50px] shrink-0"
                            onTouchStart={onTouchStart}
                            onTouchMove={onTouchMove}
                            onTouchEnd={() => onModalSwipeEnd(() => setIsModalOpen(false))}
                        >
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                className="text-gray-500 font-bold text-base"
                            >
                                닫기
                            </button>
                            <h3 className={`text-lg font-black ${theme.text} font-do-hyeon truncate`}>
                                {editingSchedule ? "일정 수정" : "새 일정 등록"}
                            </h3>
                            <button
                                type="submit"
                                className={`font-bold text-base ${theme.text}`}
                            >
                                {editingSchedule ? "수정" : "등록"}
                            </button>
                        </div>

                        {/* Mobile Content (Scrollable) */}
                        <div className="flex-1 overflow-y-auto p-6 pb-48 space-y-6 overscroll-contain min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>
                            <ScheduleFormFields
                                availableTypes={availableTypes}
                                formData={formData}
                                getTypeButtonClass={getTypeButtonClass}
                                handleAddChecklist={handleAddChecklist}
                                handleRemoveChecklist={handleRemoveChecklist}
                                handleToggleChecklist={handleToggleChecklist}
                                isMobile={isMobile}
                                newChecklistInput={newChecklistInput}
                                setFormData={setFormData}
                                setNewChecklistInput={setNewChecklistInput}
                                theme={theme}
                            />
                        </div>
                    </form>
                </div>
            </div>
        );

        const desktopLayout = (
            <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4 pointer-events-none">
                {/* Backdrop - Transparent & No Click Close */}
                <div className="absolute inset-0 bg-transparent pointer-events-auto" />

                <div
                    className="bg-white rounded-[2.5rem] w-full max-w-md shadow-2xl max-h-[85vh] flex flex-col pointer-events-auto border border-white/50 relative z-10"
                    style={{
                        transform: `translate(${modalPos.x}px, ${modalPos.y}px)`,
                        transition: isDraggingRef.current ? 'none' : 'transform 0.1s ease-out'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header - Fixed & Draggable */}
                    <div
                        className="flex justify-between items-center p-8 pb-4 border-b-2 border-gray-100 flex-shrink-0 cursor-grab active:cursor-grabbing"
                        onMouseDown={handleHeaderMouseDown}
                    >
                        <h3 className={`text-2xl font-black ${theme.text} font-do-hyeon truncate pointer-events-none select-none`}>{editingSchedule ? "일정 수정" : "새 일정 등록"}</h3>
                        <button
                            onClick={(e) => { e.stopPropagation(); setIsModalOpen(false); }}
                            className="text-gray-400 hover:text-gray-600 bg-gray-100 p-2 rounded-full flex-shrink-0 cursor-pointer"
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Content - Scrollable */}
                    <div className="overflow-y-auto custom-scrollbar p-8 pt-4">
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <ScheduleFormFields
                                availableTypes={availableTypes}
                                formData={formData}
                                getTypeButtonClass={getTypeButtonClass}
                                handleAddChecklist={handleAddChecklist}
                                handleRemoveChecklist={handleRemoveChecklist}
                                handleToggleChecklist={handleToggleChecklist}
                                isMobile={isMobile}
                                newChecklistInput={newChecklistInput}
                                setFormData={setFormData}
                                setNewChecklistInput={setNewChecklistInput}
                                theme={theme}
                            />

                            <button className={`w-full ${theme.bg} text-white py-4 rounded-2xl font-black hover:opacity-90 transition shadow-xl ${theme.shadow} mt-2 text-lg`}>저장하기</button>
                        </form>
                    </div>
                </div>
            </div>
        );

        return createPortal(
            isMobile ? mobileLayout : desktopLayout,
            document.body
        );
    };

    const renderSharingModal = () => {
        if (!sharingSchedule) return null;
        return createPortal(
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-fade-in-up" onClick={() => setSharingSchedule(null)}>
                <div className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl relative" onClick={e => e.stopPropagation()}>
                    <button onClick={() => setSharingSchedule(null)} className="absolute top-5 right-5 text-gray-400 hover:text-gray-600"><X size={24} /></button>
                    <h3 className={`text-xl font-bold text-gray-800 mb-6 font-do-hyeon`}>일정 공유</h3>
                    <div className="space-y-4">
                        <button onClick={() => handleCopySchedule(sharingSchedule)} className={`w-full py-3.5 ${theme.bg} text-white rounded-2xl font-bold hover:opacity-90 transition shadow-md flex items-center justify-center gap-2`}><Clipboard size={18} /> 내용 복사</button>
                    </div>
                </div>
            </div>,
            document.body
        );
    };

    const renderMobileBirthdaySheet = () => {
        if (!isBirthdaySheetOpen) return null;

        return createPortal(
            <div
                className="fixed inset-0 z-[9999] bg-black/35 backdrop-blur-[1px] flex items-end"
                onClick={() => setIsBirthdaySheetOpen(false)}
            >
                <div
                    className="w-full bg-white rounded-t-3xl shadow-2xl max-h-[68vh] overflow-hidden animate-slide-up"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                        <h3 className="font-black text-gray-800 text-base">
                            {currentDate.getMonth() + 1}월 생일자 {mobileMonthBirthdays.length}명
                        </h3>
                        <button
                            onClick={() => setIsBirthdaySheetOpen(false)}
                            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
                            aria-label="생일자 목록 닫기"
                        >
                            <X size={18} />
                        </button>
                    </div>
                    <div className="overflow-y-auto max-h-[calc(68vh-60px)] px-4 py-3">
                        {mobileMonthBirthdays.length === 0 ? (
                            <div className="text-center py-8 text-gray-400 text-sm">해당 월 생일자가 없습니다.</div>
                        ) : (
                            <div className="space-y-2.5">
                                {mobileMonthBirthdays.map((person) => (
                                    <button
                                        key={`${person.type}-${person.id}`}
                                        type="button"
                                        onClick={() => {
                                            setIsBirthdaySheetOpen(false);
                                            if (onNavigateToMemberDetail) onNavigateToMemberDetail(person);
                                        }}
                                        className="w-full text-left flex items-center gap-3 rounded-2xl border border-gray-100 bg-white px-3 py-2.5 shadow-sm active:scale-[0.99] transition"
                                    >
                                        <div className="w-9 h-9 rounded-xl bg-gray-100 overflow-hidden border border-gray-200 flex-shrink-0">
                                            {person.photoUrl ? (
                                                <img src={person.photoUrl} className="w-full h-full object-cover" alt="" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-xs font-black text-gray-500">{person.name?.[0] || '?'}</div>
                                            )}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-bold text-gray-800 truncate">{person.name}</div>
                                            <div className="text-[11px] text-gray-500">
                                                {person.type === 'teacher' ? '교사' : '학생'} · {person.birthday.month}/{person.birthday.day}
                                            </div>
                                        </div>
                                        <Gift size={14} className="text-rose-400 flex-shrink-0" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>,
            document.body
        );
    };

    const currentDateStr = formatDateLocal(currentDate);
    // --- 렌더링 JSX ---
    return (
        <div className={isMobile ? "bg-[#F0F0EB] min-h-screen pb-32" : "max-w-7xl mx-auto min-h-screen flex flex-col gap-6 pb-6 px-4 pt-2"}>

            {/* === 모바일 전용 UI === */}
            {isMobile && (
                <>
                    {/* 헤더 - sticky */}
                    <div className="sticky top-0 z-20 bg-[#F0F0EB]/95 backdrop-blur-md px-3 pt-4 pb-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Calendar size={20} className={theme.text} />
                            <h1 className="text-xl font-black text-gray-800">사역 캘린더</h1>
                        </div>
                        <div className="text-xs font-bold text-gray-400">일정 관리</div>
                    </div>

                    {/* 미니 캘린더 + 월 네비게이션 */}
                    <div
                        className="px-3 py-2"
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onCalendarSwipeEnd}
                    >
                        <div className="bg-white rounded-2xl p-3 border border-gray-100 shadow-sm">
                            {/* 월 네비게이션 */}
                            <div className="flex items-center justify-between mb-3">
                                <button onClick={handlePrevMonth} className="p-2 hover:bg-gray-50 rounded-xl transition">
                                    <ChevronLeft size={20} className="text-gray-600" />
                                </button>
                                <CustomDatePicker
                                    theme={theme}
                                    value={currentDateStr}
                                    onChange={handleDateChange}
                                    type="month"
                                    customTrigger={
                                        <div className="font-black text-gray-800 text-base cursor-pointer">
                                            {currentDate.getFullYear()}년 {currentDate.getMonth() + 1}월
                                        </div>
                                    }
                                />
                                <button onClick={handleNextMonth} className="p-2 hover:bg-gray-50 rounded-xl transition">
                                    <ChevronRight size={20} className="text-gray-600" />
                                </button>
                            </div>

                            {/* 간소화된 캘린더 (7일 요약) */}
                            <div className="grid grid-cols-7 gap-1">
                                {['일', '월', '화', '수', '목', '금', '토'].map((day, i) => (
                                    <div key={i} className="text-center text-[10px] font-bold text-gray-400 py-1">
                                        {day}
                                    </div>
                                ))}
                                {(() => {
                                    const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
                                    const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
                                    const days = [];

                                    // 빈 칸
                                    for (let i = 0; i < firstDay; i++) {
                                        days.push(<div key={`empty-${i}`} className="aspect-square" />);
                                    }

                                    // 날짜
                                    for (let day = 1; day <= daysInMonth; day++) {
                                        const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                        const hasSchedule = schedules.some(s => s.date === dateStr);
                                        const hasBirthday = mobileBirthdayDateSet.has(dateStr);
                                        const isToday = dateStr === new Date().toISOString().split('T')[0];

                                        days.push(
                                            <div
                                                key={day}
                                                onClick={() => handleDateTap(dateStr)}
                                                onTouchStart={() => handleDateLongPressStart(dateStr)}
                                                onTouchEnd={handleDateLongPressEnd}
                                                onMouseDown={() => handleDateLongPressStart(dateStr)}
                                                onMouseUp={handleDateLongPressEnd}
                                                onMouseLeave={handleDateLongPressEnd}
                                                className={`relative aspect-square flex items-center justify-center text-xs font-bold rounded-lg cursor-pointer transition-all
                                                        ${isSameDay(selectedDate, dateStr) ? `${theme.bg} text-white scale-110 shadow-md` : ''}
                                                        ${isToday && !isSameDay(selectedDate, dateStr) ? 'bg-blue-50 text-blue-600' : ''}
                                                        ${hasSchedule && !isSameDay(selectedDate, dateStr) ? 'bg-green-50' : ''}
                                                        ${hasBirthday && !isSameDay(selectedDate, dateStr) && !hasSchedule ? 'bg-rose-50' : ''}
                                                        ${!selectedDate && !isToday && !hasSchedule && !hasBirthday ? 'text-gray-600 hover:bg-gray-50' : ''}
                                                    `}
                                            >
                                                {day}
                                                <div className="absolute bottom-0.5 flex items-center gap-0.5">
                                                    {hasSchedule && selectedDate !== dateStr && (
                                                        <div className="w-1 h-1 rounded-full bg-green-500" />
                                                    )}
                                                    {hasBirthday && selectedDate !== dateStr && (
                                                        <div className="w-1 h-1 rounded-full bg-rose-400" />
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    }

                                    return days;
                                })()}
                            </div>
                        </div>
                    </div>

                    {/* 필터 칩 */}
                    <div className="flex items-center gap-2 px-3 py-2 overflow-x-auto">
                        <button
                            onClick={() => setIsBirthdaySheetOpen(true)}
                            className="px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap flex-shrink-0 bg-white text-rose-600 border border-rose-100 shadow-sm"
                        >
                            🎂 {currentDate.getMonth() + 1}월 생일 {mobileMonthBirthdays.length}명
                        </button>
                        {availableTypes.map(type => (
                            <button
                                key={type.id}
                                onClick={() => setSelectedType(type.id)}
                                className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap flex-shrink-0
                                        ${selectedType === type.id ? type.activeClass : 'bg-white text-gray-500 border border-gray-100'}
                                    `}
                            >
                                {type.label}
                            </button>
                        ))}
                    </div>

                    {/* 일정 리스트 */}
                    <div className="px-3 mt-2">
                        <ScheduleMobileScheduleList
                            filteredSchedulesBase={filteredSchedulesBase}
                            formatDateLocal={formatDateLocal}
                            getTypeColor={getTypeColor}
                            getTypeIcon={getTypeIcon}
                            onOpenDetail={setViewingSchedule}
                            selectedDate={selectedDate}
                            selectedType={selectedType}
                        />
                    </div>

                    <MobileFloatingActionButton
                        onClick={openAddModal}
                        className={`${theme.bg} text-white`}
                        ariaLabel="일정 추가"
                        title="일정 추가"
                    >
                        <Plus size={28} strokeWidth={2.5} />
                    </MobileFloatingActionButton>
                    {renderMobileBirthdaySheet()}
                </>
            )}

            {/* === 데스크톱 전용 UI === */}
            {
                !isMobile && (
                    <>
                        {/* [0] 타이틀 헤더 (Header Section) */}
                        <div className="hidden md:flex flex-col md:flex-row justify-between items-end gap-6">
                            <div>
                                <h2 className={`text-3xl font-black text-gray-800 flex items-center gap-3 font-do-hyeon`}>
                                    <img src={ICONS.calendar} width={48} alt="icon" className="drop-shadow-md" /> 사역 캘린더
                                </h2>
                                <p className="text-gray-500 text-sm font-medium mt-1 ml-1">우리 교회의 주요 행사와 일정을 놓치지 마세요.</p>
                            </div>
                        </div>

                        {/* [1] 상단 하이라이트 위젯 (D-Day & Birthday) - MOVED TO TOP */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-shrink-0">
                            {/* 1. D-Day Counter (Horizontal Card) */}
                            <div className={`bg-gradient-to-br from-gray-700 to-gray-800 p-5 rounded-[2.5rem] shadow-lg text-white relative overflow-hidden group min-h-[140px] flex flex-col justify-center`}>
                                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity"><img src={ICONS.fire} width={80} alt="fire" /></div>
                                <h3 className="text-base font-bold opacity-80 mb-3 flex items-center gap-2 relative z-10"><Flame size={16} className="text-orange-400" /> 주요 일정 D-Day</h3>

                                {dDayEvents.length > 0 ? (
                                    <div className="flex gap-3 overflow-x-auto custom-scrollbar pb-1 relative z-10">
                                        {dDayEvents.map(s => (
                                            <div key={s.id} className="flex-shrink-0 bg-white/10 px-4 py-3 rounded-2xl backdrop-blur-md border border-white/10 hover:bg-white/20 transition cursor-pointer flex flex-col items-center min-w-[100px]" onClick={() => setViewingSchedule(s)}>
                                                <div className="text-xs opacity-80 mb-1">{s.date.slice(5)}</div>
                                                <div className="font-bold text-sm truncate w-full text-center mb-1">{s.title}</div>
                                                <div className="text-xl font-black font-do-hyeon text-orange-300">
                                                    {s.dDay === 0 ? "D-Day" : `D-${s.dDay}`}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (

                                    <div className="flex flex-col items-center justify-center opacity-60 py-2">
                                        <img src={ICONS.fire} width={48} className=" grayscale opacity-50 mb-1" alt="empty" />
                                        <div className="text-xs font-medium text-white/70">예정된 주요 일정이 없습니다.</div>
                                    </div>
                                )}
                            </div>

                            {/* 2. 이번 달 생일자 (Horizontal Card) */}
                            <div className="bg-white/60 backdrop-blur-xl p-5 rounded-[2.5rem] shadow-sm border border-white/40 min-h-[140px] flex flex-col justify-center">
                                <h3 className="text-base font-black text-gray-800 mb-3 flex items-center gap-2">
                                    <img src={ICONS.cake} width={20} alt="cake" />
                                    이번 달 생일 <span className="text-xs font-bold text-gray-400 bg-white px-2 py-0.5 rounded-lg shadow-sm">{upcomingBirthdays.length}명</span>
                                </h3>
                                {upcomingBirthdays.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-2 opacity-60">
                                        <img src={ICONS.cake} width={48} className="grayscale opacity-50 mb-1" alt="empty" />
                                        <div className="text-gray-400 text-xs font-bold pl-2">이번 달 생일자가 없습니다 🎉</div>
                                    </div>
                                ) : (
                                    <div className="flex gap-3 overflow-x-auto custom-scrollbar pb-1">
                                        {upcomingBirthdays.map((p, i) => (
                                            <div key={i} className="flex-shrink-0 flex items-center gap-2 bg-white p-2 rounded-2xl shadow-sm border border-gray-50 pr-4">
                                                <div className="w-10 h-10 rounded-xl bg-gray-50 overflow-hidden border border-gray-100 flex-shrink-0">
                                                    {p.photoUrl ? <img src={p.photoUrl} className="w-full h-full object-cover" alt="" /> : <div className="w-full h-full flex items-center justify-center text-xs font-bold text-gray-400">{p.name[0]}</div>}
                                                </div>
                                                <div>
                                                    <div className="font-bold text-gray-800 text-xs">{p.name}</div>
                                                    <div className="text-[10px] text-rose-500 font-bold flex items-center gap-1"><Gift size={8} /> {p.birth.slice(5)}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* [2] 툴바 (Sticky Floating Control Deck) */}
                        <ScheduleToolbarControls
                            availableTypes={availableTypes}
                            currentDate={currentDate}
                            currentDateStr={currentDateStr}
                            onAdd={() => openAddModal()}
                            onDateChange={handleDateChange}
                            onNextMonth={handleNextMonth}
                            onPrevMonth={handlePrevMonth}
                            onSelectType={setSelectedType}
                            onSetViewMode={setViewMode}
                            selectedType={selectedType}
                            theme={theme}
                            viewMode={viewMode}
                        />

                        {/* 메인 컨텐츠 그리드 */}
                        {/* 메인 컨텐츠 그리드 */}
                        <div className={`flex-1 grid gap-6 min-h-0 transition-all duration-500 ${viewMode === 'calendar' ? 'grid-cols-1 lg:grid-cols-[1fr_350px]' : 'grid-cols-1'}`}>

                            {/* [좌측/메인] 대형 캘린더 (달력 보기일 때만 표시) */}
                            <div className={`bg-white/60 backdrop-blur-xl p-6 rounded-[2.5rem] shadow-sm border border-white/40 flex flex-col h-full min-h-0 transition-all duration-500 overflow-x-auto ${viewMode === 'calendar' ? 'opacity-100 translate-x-0' : 'hidden opacity-0 -translate-x-10'}`}>
                                <div className="min-w-[600px]">
                                    <div className="grid grid-cols-7 mb-4 text-center flex-shrink-0">
                                        {['일', '월', '화', '수', '목', '금', '토'].map((day, i) => (
                                            <div key={day} className={`text-sm font-black uppercase tracking-wider ${i === 0 ? 'text-rose-500' : i === 6 ? 'text-blue-500' : 'text-gray-400'}`}>{day}</div>
                                        ))}
                                    </div>

                                    <div className="grid grid-cols-7 gap-3 flex-1 min-h-[400px] auto-rows-fr" style={{ gridAutoRows: '1fr' }}>
                                        {getDaysInMonth(currentDate).map((date, idx) => {
                                            if (!date) return <div key={idx} className="bg-white/20 border border-transparent rounded-[1.5rem]"></div>;

                                            const isSelected = isSameDay(date, selectedDate);
                                            const isToday = isSameDay(date, new Date());
                                            const dateStr = formatDateLocal(date);
                                            const daySchedules = filteredSchedulesBase.filter(s => s.date === dateStr);
                                            const hasEvent = daySchedules.length > 0;

                                            const dayOfWeek = date.getDay();
                                            const isSunday = dayOfWeek === 0;
                                            const isSaturday = dayOfWeek === 6;
                                            const holidayName = getHolidayName(date);

                                            return (
                                                <div
                                                    key={idx}
                                                    onClick={() => handleDayClick(date)}
                                                    onDoubleClick={() => handleDayDoubleClick(date)}
                                                    className={`
                                            min-h-[90px]
                                            rounded-[1.5rem] flex flex-col items-start justify-start p-2 cursor-pointer transition-all duration-300 relative border group
                                            ${isSelected
                                                            ? `bg-white ${theme.border} ring-4 ${theme.border.replace('border-', 'ring-').replace(']', ']/10')} shadow-lg z-10 scale-[1.02]`
                                                            : isToday
                                                                ? "bg-white/80 border-gray-200 shadow-inner"
                                                                : "bg-white/40 border-transparent hover:bg-white hover:shadow-md hover:-translate-y-1"
                                                        }
                                        `}
                                                >
                                                    <div className="flex justify-between items-start w-full relative z-10">
                                                        <span className={`text-sm font-bold ml-1 mt-1 w-7 h-7 flex items-center justify-center rounded-full 
                                                    ${isToday ? `${theme.bg} text-white shadow-md` : isSelected ? `${theme.text}` : isSunday || holidayName ? 'text-rose-500' : isSaturday ? 'text-blue-500' : 'text-gray-600'}`}>
                                                            {date.getDate()}
                                                        </span>
                                                        {holidayName && <span className="text-[10px] font-bold text-rose-500 mt-2 mr-2 truncate max-w-[50px]">{holidayName}</span>}
                                                    </div>

                                                    {/* Hover Add Overlay */}
                                                    <div className="absolute inset-0 bg-black/5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-0 rounded-[1.5rem]">
                                                        <Plus size={24} className="text-gray-400/50" />
                                                    </div>

                                                    {/* 일정 아이콘들 */}
                                                    <div className="flex flex-col gap-1 mt-1 w-full px-1">
                                                        {daySchedules.slice(0, 4).map((s, i) => (
                                                            <div key={i} className="w-full">
                                                                <div className={`text-[10px] truncate px-2 py-1 rounded-lg font-bold flex items-center gap-1 shadow-sm transition-transform hover:scale-105
                                                            ${s.isCompleted ? 'bg-gray-100 text-gray-400 line-through' : s.type === 'worship' ? 'bg-purple-50 text-purple-600' : s.type === 'event' ? 'bg-orange-50 text-orange-600' : `${theme.lightBg} ${theme.text}`}`}>
                                                                    {s.priority === 'high' && <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse flex-shrink-0"></div>}
                                                                    {s.startTime && <span className="text-[9px] mr-1 opacity-70 hidden sm:inline">{s.startTime}</span>}
                                                                    {selectedDepartment === 'All' && (
                                                                        <span className={`text-[9px] text-white px-1.5 py-[1px] rounded-[4px] inline-block mr-1.5 whitespace-nowrap font-bold shadow-sm ${getDepartmentColor(s.department || 'All')}`}>
                                                                            {s.department === 'All' || !s.department ? '전체' : s.department}
                                                                        </span>
                                                                    )}
                                                                    <span className="truncate">{s.title}</span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                        {daySchedules.length > 4 && (
                                                            <div className="text-[10px] text-gray-400 font-bold pl-2">+{daySchedules.length - 4}개 더보기</div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                </div>
                            </div>

                            {/* [우측] 스마트 사이드바 */}
                            <div className="flex flex-col gap-6 h-full min-h-0 overflow-y-auto custom-scrollbar pr-1">

                                {/* 선택된 날짜/오늘의 일정 리스트 (D-Day, Birthday removed from here) */}
                                <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[2.5rem] shadow-sm border border-white/40 flex-1 flex flex-col min-h-0 h-full">
                                    <div className="flex justify-between items-center mb-4 flex-shrink-0">
                                        <div className="flex items-center gap-2">
                                            <h3 className="text-lg font-black text-gray-800 flex items-center gap-2">
                                                <img src={ICONS.memo} width={24} alt="memo" />
                                                {selectedDate ? `${(selectedDate instanceof Date ? selectedDate : new Date(selectedDate)).getDate()}일 일정` : "전체 일정"}
                                            </h3>
                                            {selectedDate && (
                                                <button
                                                    onClick={() => setSelectedDate(null)}
                                                    className={`text-[10px] font-bold text-gray-400 hover:${theme.text} bg-gray-100 px-2 py-1 rounded-lg transition flex items-center gap-1`}
                                                >
                                                    <RotateCcw size={10} /> 전체보기
                                                </button>
                                            )}
                                        </div>
                                    </div>



                                    <div className={`flex-1 overflow-y-auto custom-scrollbar pr-1 ${viewMode === 'card' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-max content-start' : 'space-y-3'}`}>
                                        {filteredSchedules.length === 0 ? (
                                            <div className="col-span-full h-full flex flex-col items-center justify-center text-gray-400 opacity-60">
                                                <p className="text-sm font-bold">일정이 없습니다.</p>
                                                {selectedDate && <button onClick={() => openAddModal()} className={`mt-2 text-xs ${theme.text} hover:underline`}>새 일정 만들기</button>}
                                            </div>
                                        ) : (
                                            filteredSchedules.map((schedule) => (
                                                viewMode === 'calendar' ? (
                                                    <ScheduleListItem
                                                        key={schedule.id}
                                                        getDepartmentColor={getDepartmentColor}
                                                        onOpenDetail={setViewingSchedule}
                                                        onToggleComplete={handleToggleComplete}
                                                        schedule={schedule}
                                                        selectedDepartment={selectedDepartment}
                                                        theme={theme}
                                                    />
                                                ) : (
                                                    <ScheduleCardItem
                                                        key={schedule.id}
                                                        fireIconSrc={ICONS.fire}
                                                        getDepartmentColor={getDepartmentColor}
                                                        getTypeIcon={getTypeIcon}
                                                        getTypeName={getTypeName}
                                                        onOpenDetail={setViewingSchedule}
                                                        schedule={schedule}
                                                        selectedDepartment={selectedDepartment}
                                                    />
                                                )
                                            ))
                                        )}
                                    </div>
                                </div>

                            </div>
                        </div>
                    </>
                )
            }

            {/* === 모달들 (모바일/데스크톱 공통) === */}
            {renderEditModal()}
            {/* [REFACTORED] Reusable Detail Modal */}
            <ScheduleDetailModal
                schedule={viewingSchedule}
                onClose={() => setViewingSchedule(null)}
                onEdit={switchToEdit}
                onDelete={(id) => { onDelete(id); setViewingSchedule(null); }}
                onShare={(s) => { setViewingSchedule(null); setSharingSchedule(s); }}
                onChecklistToggle={handleDetailChecklistToggle}
                theme={theme}
                userProfile={userProfile}
            />
            {renderSharingModal()}
        </div >
    );
}
